import { GiftOutlined, UserOutlined } from '@ant-design/icons';
import { App, Button, Form, Input, InputNumber, Spin, Statistic } from 'antd';
import vs from 'assets/images/vs.png';
import Header from 'components/Header/Header';
import HistoryPredictions from 'components/HistoryPredictions';
import { SITE } from 'config/config';
import dayjs from 'dayjs';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import {
  createPredictions,
  getAllSetting,
  getFP,
  getIP,
  getMatchList,
  getTime,
  ssoVerify
} from 'service/base';
import { twMerge } from 'tailwind-merge';
import { CONST_SITE, LocalStorageKey } from 'utils/common/const';
import RulePage from './rule';

const SSO_PARAMS = ['playerid', 'timestamp', 'nonce', 'token', 'sign'];

const daysOfWeek = {
  Monday: 'Thứ Hai',
  Tuesday: 'Thứ Ba',
  Wednesday: 'Thứ Tư',
  Thursday: 'Thứ Năm',
  Friday: 'Thứ Sáu',
  Saturday: 'Thứ Bảy',
  Sunday: 'Chủ Nhật'
};

const leagues = [
  {
    id: 1,
    name: 'UEFA Champions League',
    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/f/f5/UEFA_Champions_League.svg/1024px-UEFA_Champions_League.svg.png'
  },
  {
    id: 2,
    name: 'UEFA Europa League',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Europa_League_2021.svg/795px-Europa_League_2021.svg.png'
  },
  {
    id: 3,
    name: 'English Premier League',
    logo: 'https://loodibee.com/wp-content/uploads/Premier-League-Logo.png'
  },
  {
    id: 4,
    name: 'La Liga',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0f/LaLiga_logo_2023.svg/1200px-LaLiga_logo_2023.svg.png'
  },
  {
    id: 5,
    name: 'Italian Serie A',
    logo: 'https://1000logos.net/wp-content/uploads/2019/01/Italian-Serie-A-Logo.png'
  },
  {
    id: 6,
    name: 'German Bundesliga',
    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/df/Bundesliga_logo_%282017%29.svg/1200px-Bundesliga_logo_%282017%29.svg.png'
  },
  {
    id: 7,
    name: 'France Ligue 1',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Logo_Ligue_1_McDonald%27s_2024.svg/250px-Logo_Ligue_1_McDonald%27s_2024.svg.png'
  }
];

const PredictPage = () => {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const accountValue = Form.useWatch('account', form);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [listDataMatch, setListDataMatch] = useState([]);
  const [timeServer, setTimeServer] = useState(null);
  const [IP, setIP] = useState(null);
  const [FP, setFP] = useState(null);
  const [dataSettings, setDataSettings] = useState([]);
  const [isModalHistory, setIsModalHistory] = useState(false);
  const [isModalRule, setIsModalRule] = useState(false);
  const [isSsoLoggedIn, setIsSsoLoggedIn] = useState(() => {
    try {
      const token = localStorage.getItem(LocalStorageKey.ACCESS_TOKEN);
      const username = localStorage.getItem('78win_username');
      return !!(token && username);
    } catch {
      return false;
    }
  });

  const [selectedLeagues, setSelectedLeagues] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'today', 'tomorrow'

  // Hàm lọc trận đấu theo tab được chọn
  const getFilteredMatches = () => {
    if (!listDataMatch || listDataMatch.length === 0) return [];

    const now = dayjs();
    const today = now.format('YYYY-MM-DD');
    const tomorrow = now.add(1, 'day').format('YYYY-MM-DD');

    switch (activeTab) {
      case 'today':
        return listDataMatch.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === today;
        });
      case 'tomorrow':
        return listDataMatch.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === tomorrow;
        });
      case 'all':
      default:
        return listDataMatch;
    }
  };

  useEffect(() => {
    fetchAllSetting();
    fetchIP();
    fetchFP();
    fetchTimeServer();
  }, []);

  useEffect(() => {
    fetchDataScheduByLS();
  }, []);

  // Restore SSO account on mount (e.g. after page reload or navigation)
  useEffect(() => {
    if (!isSsoLoggedIn) return;
    try {
      const username = localStorage.getItem('78win_username');
      if (username) {
        form.setFieldsValue({ account: username });
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSO login: when redirected with SSO query params, verify and store token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const playerId = params.get('playerId');
    const timestamp = params.get('timestamp');
    const nonce = params.get('nonce');
    const token = params.get('token');
    const signature = params.get('signature');


    if (!playerId || !timestamp || !nonce || !token || !signature) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await ssoVerify({ playerid: playerId, timestamp, nonce, token,  sign: signature });

        if (cancelled) return;

        const userToken = res?.data?.userToken || null;

        if (userToken) {
          localStorage.setItem(LocalStorageKey.ACCESS_TOKEN, userToken);
          try {
            localStorage.setItem('78win_username', playerId);
          } catch {
            // ignore
          }
          form.setFieldsValue({ account: playerId });
          setIsSsoLoggedIn(true);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[SSO] verify failed:', err);
      } finally {
        if (!cancelled) {
          // Maybe have clean SSO params from URL
          // for (const key of SSO_PARAMS) params.delete(key);
          // const remaining = params.toString();
          // const cleanUrl = remaining
          //   ? `${window.location.pathname}?${remaining}`
          //   : window.location.pathname;
          // window.history.replaceState({}, '', cleanUrl);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchIP = async () => {
    try {
      const res = await getIP();
      setIP(res);
    } catch (e) {}
  };

  const fetchFP = async () => {
    try {
      const res = await getFP();
      setFP(res);
    } catch (e) {}
  };

  const fetchTimeServer = async () => {
    try {
      const res = await getTime();
      setTimeServer(res);
    } catch (e) {}
  };

  const fetchDataScheduByLS = async () => {
    try {
      setIsLoading(true);
      const res2 = await getMatchList({
        site: CONST_SITE.OK9
      });

      const dataMatch2 =
        res2?.data?.length > 0
          ? res2.data.map((item) => {
              const formatCountDown = dayjs.unix(item.startTime);
              return { ...item, countdown: new Date(formatCountDown) };
            })
          : [];

      setListDataMatch(dataMatch2 || []);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching match list:', error);
      setListDataMatch([]);
      setIsLoading(false);
    }
  };

  const fetchAllSetting = async (data) => {
    try {
      setIsLoading(true);
      const res = await getAllSetting(qs.stringify({ site: CONST_SITE.OK9 }));
      setDataSettings(res?.data || []);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const TIMEOUT_DURATION = 10000; // 10 seconds

  const createPredictionsWithTimeout = (predictions) => {
    return Promise.race([
      createPredictions(predictions),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out')), TIMEOUT_DURATION)
      )
    ]);
  };

  const onFinishForm = async (data) => {
    try {
      const filteredMatches = getFilteredMatches();
      if (filteredMatches?.length === 0) return;

      if (!data?.account) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng nhập tên đăng nhập!'
        });
        return;
      }

      // Validate that both home and away scores are provided for each match
      const hasInvalidScores = Object.entries(data).some(([key, value]) => {
        // Bỏ qua field account và các field không phải score
        if (key === 'account' || !key.includes('_')) return false;

        const parts = key.split('_');
        if (parts.length !== 2) return false;

        const [scoreType, matchId] = parts;
        if (!['homeScore', 'awayScore'].includes(scoreType)) return false;

        const otherScoreType = scoreType === 'homeScore' ? 'awayScore' : 'homeScore';
        const otherScoreKey = `${otherScoreType}_${matchId}`;
        const otherValue = data[otherScoreKey];

        // Kiểm tra nếu một trong hai score có giá trị nhưng score kia không có
        const hasValue = value !== undefined && value !== null && value !== '';
        const hasOtherValue = otherValue !== undefined && otherValue !== null && otherValue !== '';

        if (hasValue && !hasOtherValue) {
          return true;
        }

        return false;
      });

      if (hasInvalidScores) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng nhập đầy đủ cả hai bên tỉ số cho mỗi trận đấu!'
        });
        return;
      }

      setIsLoadingSubmit(true);

      console.log('Submitting data:', data);
      console.log('Filtered Matches:', filteredMatches);
      const resultGroup = groupScoresByMatchId(data, IP, FP, filteredMatches);
      console.log('Grouped Results:', resultGroup);

      if (resultGroup.length === 0) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng dự đoán ít nhất một trận đấu!'
        });
        setIsLoadingSubmit(false);
        return;
      }

      const res = await createPredictionsWithTimeout(resultGroup);
      if (res?.code === 0) {
        notification.success({
          message: 'Thành công',
          description: 'Dự đoán tỉ số thành công!'
        });
        form.resetFields(Object.keys(form.getFieldsValue()).filter((key) => key !== 'account'));
      } else {
        if (res.valid === false) {
          notification.error({
            message: res.title_mess,
            description: res?.text_mess
          });
        } else {
          notification.error({
            message: 'Lỗi',
            description: res?.message
          });
        }
      }
      setIsLoadingSubmit(false);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Đã xảy ra lỗi khi gửi dự đoán!'
      });
      setIsLoadingSubmit(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    if (!errorInfo?.values?.account) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng nhập tên đang nhập!'
      });
    } else {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng điền đầy đủ thông tin!'
      });
    }
  };

  const groupScoresByMatchId = (input, ip, fp, matchInfo) => {
    const groupedMatches = Object.entries(input).reduce((acc, [key, value]) => {
      console.log('Processing key:', key, 'with value:', value);
      if (key === 'account') return acc;

      const parts = key.split('_');
      if (parts.length !== 2) return acc;

      const [scoreType, matchId] = parts;
      if (!matchId || !['homeScore', 'awayScore'].includes(scoreType)) return acc;

      // Kiểm tra giá trị hợp lệ
      if (value === undefined || value === null || value === '') return acc;

      if (!acc[matchId]) {
        acc[matchId] = {
          matchId,
          account: input.account,
          homeScore: undefined,
          awayScore: undefined
        };
      }

      if (scoreType === 'homeScore') {
        acc[matchId].homeScore = value;
      } else if (scoreType === 'awayScore') {
        acc[matchId].awayScore = value;
      }

      return acc;
    }, {});

    console.log('Grouped Matches:', groupedMatches);

    return Object.values(groupedMatches)
      .filter(
        (match) =>
          match.homeScore !== undefined &&
          match.awayScore !== undefined &&
          match.homeScore !== null &&
          match.awayScore !== null &&
          match.homeScore !== '' &&
          match.awayScore !== ''
      )
      .map(({ matchId, account, homeScore, awayScore }) => {
        const matchDetails = matchInfo.find(
          (match) => parseInt(match.matchId) === parseInt(matchId)
        );

        return {
          matchId,
          homeScore,
          awayScore,
          account,
          ip,
          fp,
          homeName: matchDetails ? matchDetails.homeName : 'Unknown',
          awayName: matchDetails ? matchDetails.awayName : 'Unknown',
          site: SITE
        };
      });
  };

  const onFinishCountDown = () => {
    console.log('finished!');
  };

  const showListItemMatch = (matchList) => {
    console.log('matchList:', matchList);
    if (!matchList || matchList.length <= 0) {
      return <p className="text-center">Hiện tại không có trận đấu nào để dự đoán</p>;
    } else {
      const filteredMatchList =
        selectedLeagues.length > 0
          ? matchList.filter((item) =>
              selectedLeagues.includes(
                leagues.find((league) => league.name === item.leagueName)?.id
              )
            )
          : matchList;

      const itemMatch = filteredMatchList
        .filter((item) => timeServer <= item.countdown) // Filter out matches that have ended
        .map((item, index) => {
          const matchDayOfWeek = daysOfWeek[dayjs(item.startTime * 1000).format('dddd')]; // Calculate day of week for each match
          return (
            <div
              className={twMerge(
                'flex items-center xl:w-3/4 md:w-4/5 w-full mx-auto bg-white rounded-2xl px-5 text-sm font-medium gap-2 border flex-col shadow-[0_0_10.574px_0_rgba(0,0,0,0.09)_inset] pb-3 mt-2',
                timeServer > item.countdown ? 'contrast-50' : ''
              )}
              key={index}
              style={{
                backgroundImage: `url(assets/images/match/bg.png)`,
                backgroundSize: 'cover'
              }}
            >
              <div
                className={twMerge(
                  'italic text-center w-auto ',
                  'px-5 md:px-10',
                  'rounded-b-[10.448px]',
                  'bg-[#737373]',
                  'shadow-[0_2.09px_10.448px_0_rgba(0,0,0,0.25)]'
                )}
              >
                <p className="text-white text-center font-roboto font-bold leading-normal pt-2 pb-2 not-italic text-xs md:text-base lg:text-lg">
                  {item?.leagueName}
                </p>
              </div>
              <div className="flex flex-col md:flex-row justify-center md:justify-around gap-0 md:gap-6 w-full items-center text-xs sm:text-sm md:text-base pb-3">
                <div className="flex flex-row items-center ">
                  <span className="flex flex-row items-center h-full">
                    <span
                      className={`mr-1 flex items-center h-full md:text-[16px] text-[14px] ${
                        timeServer > item.countdown ? 'text-red-500' : 'text-black'
                      }`}
                    >
                      {timeServer > item.countdown ? 'Hết giờ' : 'Thời gian dự đoán còn: '}
                    </span>
                    <Statistic.Countdown
                      value={item.countdown}
                      onFinish={onFinishCountDown}
                      className="text-[#0042CB] ml-1 font-semibold flex items-center"
                    />
                  </span>
                </div>
                <span className="flex items-center text-black">
                  <span className="flex items-center h-full md:text-[16px] text-[14px]">
                    Thời gian kết thúc:{' '}
                  </span>
                  <span className="text-[#0042CB] ml-1 font-semibold flex items-center h-full  md:text-[16px] text-[14px]">
                    {dayjs(item.startTime * 1000).format('HH:mm')}
                  </span>
                </span>
              </div>
              <div className="rounded-t-[5.287px] bg-[linear-gradient(180deg,#53ADFF_0%,#0066FF_100%)] flex w-auto min-w-[180px] sm:w-[210px] h-[28px] sm:h-[32px] px-3 sm:px-4 py-1.5 sm:py-2 justify-center items-center shrink-0">
                <p className="text-white text-center text-xs sm:text-sm md:text-base font-semibold leading-normal whitespace-nowrap">
                  {`${matchDayOfWeek}, ${dayjs(item.startTime * 1000).format('DD/MM/YYYY HH:mm')}`}
                </p>
              </div>
              <div className="flex flex-row item-center justify-center mt-[-16px] md:mt-[-20px] h-[50px] md:h-[70px] w-full xl:w-3/4 relative ">
                <div className="absolute w-[95%] h-[70%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 !z-0 bg-gradient-to-b from-[#53ADFF] to-[#0066FF]" />
                <div
                  className="z-[1] flex justify-center items-center w-full  rounded-full bg-white shadow-[inset_0_0_5.432px_0_rgba(0,0,0,0.25)] 
                                    md:max-w-[70px] md:h-[70px] md:p-[5px]
                                    max-w-[50px] h-[50px] p-[2px]"
                  style={{ border: '1px solid #0066FF' }}
                >
                  <img src={item.homeLogo} className="w-[75%] h-full object-contain" alt="" />
                </div>

                <div className="text-xs sm:text-sm md:text-base font-medium flex justify-center items-center w-full h-full z-[1] text-white px-1">
                  {item.homeName}
                </div>

                <div className="items-center absolute w-[15%] h-[75%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 justify-center z-[1] ">
                  <span
                    className="font-bold flex items-center justify-center text-lg sm:text-2xl md:text-3xl lg:text-4xl px-3 sm:px-4 md:px-5 text-white w-full h-full"
                    style={{
                      backgroundImage: `url(${vs})`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center'
                    }}
                  >
                    VS
                  </span>
                </div>
                <div className="text-xs sm:text-sm md:text-base font-medium flex justify-center items-center w-full h-full z-[1] text-white px-1">
                  {item.awayName}
                </div>

                <div
                  className="z-[1] flex justify-center items-center w-full rounded-full bg-white shadow-[inset_0_0_5.432px_0_rgba(0,0,0,0.25)] 
                                    md:max-w-[70px] md:h-[70px] md:p-[5px]
                                    max-w-[50px] h-[50px] p-[2px]"
                  style={{ border: '1px solid #0066FF' }}
                >
                  <img src={item.awayLogo} className="w-[75%] h-full object-contain" alt="" />
                </div>
              </div>
              <div className="flex flex-col w-full sm:w-3/4 justify-center items-center px-2">
                <div className="flex md:justify-around justify-center flex-row items-center p-1 gap-3 sm:gap-5 md:gap-16">
                  <Form.Item className="m-0 p-0" name={`homeScore_${item.matchId}`}>
                    <InputNumber
                      disabled={timeServer > item.countdown ? true : false}
                      controls={false}
                      min={0}
                      max={99}
                      maxLength={2}
                      placeholder="Nhập số"
                      className="bg-white border-0 w-[70px] sm:w-[90px] md:w-[145px] h-[34px] sm:h-[37px] md:h-[40px] text-center text-xs sm:text-sm md:text-base font-normal text-[#505050] shadow-[0px_1.057px_4.23px_0px_inset_rgba(0,0,0,0.29)] rounded-md"
                      style={{
                        textAlign: 'center',
                        fontFamily: "'Roboto', sans-serif"
                      }}
                    />
                  </Form.Item>
                  <span className="font-bold text-black text-sm sm:text-base md:text-lg whitespace-nowrap px-1">
                    Tỉ số
                  </span>
                  <Form.Item className="m-0 p-0" name={`awayScore_${item.matchId}`}>
                    <InputNumber
                      disabled={timeServer > item.countdown ? true : false}
                      controls={false}
                      min={0}
                      max={99}
                      maxLength={2}
                      placeholder="Nhập số"
                      className="bg-white border-0 w-[70px] sm:w-[90px] md:w-[145px] h-[34px] sm:h-[37px] md:h-[40px] text-center text-xs sm:text-sm md:text-base font-normal text-[#505050] shadow-[0px_1.057px_4.23px_0px_inset_rgba(0,0,0,0.29)] rounded-md"
                      style={{
                        textAlign: 'center',
                        fontFamily: "'Roboto', sans-serif"
                      }}
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="flex flex-row w-full sm:w-3/4 justify-center mt-2">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="rounded-[40px] border-[0.8px] bg-gradient-to-br from-[#53ADFF] to-[#0066FF] flex w-[140px] sm:w-[163px] h-[38px] sm:h-[40px] md:h-[44px] px-4 sm:px-5 py-2.5 sm:py-3 justify-center items-center text-white font-semibold text-sm sm:text-base"
                >
                  <span className="flex items-center justify-center w-full">XÁC NHẬN</span>
                </Button>
              </div>
            </div>
          );
        });
      return itemMatch;
    }
  };

  return (
    <div className={twMerge(SITE, 'w-full h-full')}>
      <img
        src="bg.png"
        alt="Background"
        className="w-full h-full fixed right-0 bottom-0 object-cover object-center"
      />
      <div className="hidden xl:block">
        <div className="fixed flex flex-col items-center justify-center left-20 top-20 w-32 md:w-48 lg:w-60">
          <img src="assets/images/fifa.png" alt="Background" className="object-contain" />
          <img
            src="assets/images/zayu_left.png"
            alt="Background"
            className="mt-32 object-contain"
          />
        </div>
        <div className="fixed flex flex-col items-center justify-center right-20 top-20 w-32 md:w-48 lg:w-60">
          <img src="assets/images/worldcup.png" alt="Background" className="object-contain" />
          <img
            src="assets/images/zayu_right.png"
            alt="Background"
            className="mt-20 object-contain"
          />
        </div>
      </div>
      <div className="container pb-3 px-3 relative z-10">
        <Header />
        <div className="mx-auto">
          <div className="w-full h-full">
            <Form
              name="formMatch"
              onFinish={onFinishForm}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
              form={form}
              initialValues={{ account: '' }}
            >
              <div className="w-full h-full">
                <div className="flex flex-col bg-white rounded-lg border-solid border-[#0042CB] border-[2px] shadow shadow-primary p-2 py-5 w-full md:px-6 h-full items-center">
                  <div className="flex flex-col sm:flex-row w-full h-full items-center px-3 justify-between gap-2 md:mx-3">
                    <div className="flex gap-2 md:py-4">
                      {isSsoLoggedIn ? (
                        <>
                          <Form.Item name="account" hidden>
                            <Input />
                          </Form.Item>
                          <div
                            className="flex items-center gap-2 h-[32px] min-w-[132px] max-w-[220px] px-[11px] rounded-[6px] text-white text-sm font-medium border-0"
                            style={{
                              background: 'linear-gradient(180deg, #53ADFF 0%, #0066FF 100%)'
                            }}
                          >
                            <UserOutlined className="text-white shrink-0" />
                            <span className="truncate">{accountValue}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <Form.Item
                            name="account"
                            className="mb-0"
                            rules={[
                              {
                                required: true,
                                message: 'Vui lòng điền tên đăng nhập!'
                              }
                            ]}
                          >
                            <Input
                              placeholder="Tên đăng nhập ..."
                              maxLength={20}
                              prefix={<UserOutlined />}
                            />
                          </Form.Item>
                          <Form.Item className="mb-0">
                            <Button
                              type="primary"
                              htmlType="submit"
                              loading={isLoadingSubmit}
                              className="text-white font-medium rounded-[4.286px] border-0"
                              style={{
                                background: 'linear-gradient(180deg, #53ADFF 0%, #0066FF 100%)'
                              }}
                            >
                              Xác Nhận
                            </Button>
                          </Form.Item>
                        </>
                      )}
                    </div>
                    <div className="flex gap-2 md:py-4">
                      <Button
                        type="primary"
                        className="text-white font-medium rounded-[12px] border-0"
                        onClick={() =>
                          window.open(
                            'https://78winer.pro/LIVE.D%E1%BB%B0.%C4%90O%C3%81N.WC',
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }
                        style={{
                          background: 'linear-gradient(180deg, #53ADFF 0%, #0066FF 100%)'
                        }}
                      >
                        Xem live
                      </Button>
                    </div>
                  </div>
                  <p className="w-full mt-3 px-3 text-black text-sm md:text-base font-normal italic">
                    Nhập tên đăng nhập và tỷ số ít nhất 1 trận để dự đoán.
                  </p>

                  <div className="flex w-full h-full mt-2 items-center px-3 justify-between gap-2">
                    <Button
                      type="primary"
                      onClick={() => setIsModalHistory(true)}
                      className="text-white font-medium rounded-[4.286px] border-0"
                      style={{
                        background: 'linear-gradient(180deg, #53ADFF 0%, #0066FF 100%)'
                      }}
                    >
                      Lịch sử dự đoán
                    </Button>
                    <Button
                      type="primary"
                      icon={<GiftOutlined />}
                      onClick={() => setIsModalRule(true)}
                      className="text-white font-medium rounded-[4.286px] border-0"
                      style={{
                        background: 'linear-gradient(180deg, #53ADFF 0%, #0066FF 100%)'
                      }}
                    >
                      Cơ cấu giải thưởng
                    </Button>
                  </div>
                </div>

                <div className="mx-auto mt-5 bg-white rounded-lg border-solid border-[#0042CB] border-[2px] shadow shadow-primary p-2 w-full md:pb-8 md:pt-4">
                  <div className="w-full h-full">
                    {isLoading ? (
                      <div className="w-full h-full text-center py-8">
                        <Spin />
                      </div>
                    ) : (
                      <div className="w-full h-full flex flex-col gap-2 mt-2">
                        {showListItemMatch(getFilteredMatches())}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </div>

        {/* <div className="mx-auto mt-5 bg-white rounded-lg border-solid border-[#0042CB] border-[2px] shadow shadow-primary p-2 w-full md:pb-8 md:pt-4">
          <div className="w-full h-full">
            {isLoading ? (
              <div className="w-full h-full text-center py-8">
                <Spin />
              </div>
            ) : (
              <div className="w-full h-full flex flex-col gap-2 mt-2">
                {showListItemMatch(getFilteredMatches())}
              </div>
            )}
          </div>
        </div> */}

        {/* Lịch Thi Đấu */}
        <div className="bg-white mt-5 mb-3 border-solid border-[#0042CB] border-[2px] rounded-lg w-full max-h-[90vh] overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-center p-4">
            <h2 className="text-xl font-bold text-[#0042CB]">LỊCH THI ĐẤU HOT</h2>
          </div>

          {/* Content */}
          <div className="p-5 border-none border-0">
            <iframe
              src="https://football-widget.pages.dev/"
              //   width="100%"
              height="600"
              //   frameBorder="0"
              //   allowFullScreen
              title="Football Widget"
              className="w-full border-0 border-none rounded-lg"
            />
          </div>
        </div>
      </div>

      <HistoryPredictions
        isModalVote={isModalHistory}
        setIsModalVote={setIsModalHistory}
        IP={IP}
        FP={FP}
      />
      <RulePage isModalRule={isModalRule} setIsModalRule={setIsModalRule} />
    </div>
  );
};

export default PredictPage;
